import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { useDebouncedSave } from "../hooks/useDebouncedSave";
import { useSaveField } from "../hooks/useSaveField";
import { FrameworkAgreementField } from "../components/FrameworkAgreementField";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { ContractPeriodSection } from "../components/ContractPeriodSection";
import { AdvancedSettingsSection } from "../components/AdvancedSettingsSection";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { ContractNature, Currency, type SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { CONTRACT_NATURE_OPTIONS } from "../utils/publicProcurementOptions";
import { getThresholdInfo } from "../utils/getThresholdInfo";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  nextStep: () => void;
}

export const InfoStep = ({ eventId, event, nextStep }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);

  const [title, setTitle] = useState(event.eventData.title);
  const [estimatedValue, setEstimatedValue] = useState<number | null>(event.publicContext.estimatedValue?.value ?? null);
  const [contractNature, setContractNature] = useState<ContractNature | "">(event.publicContext.contractNature ?? "");
  const [cpvCode, setCpvCode] = useState(event.publicContext.cpvClassification?.mainCode ?? "");

  useEffect(() => {
    setTitle(event.eventData.title);
  }, [event.eventData.title]);

  const debouncedSaveTitle = useDebouncedSave(
    useCallback((value: string) => saveField({ title: value }), [saveField])
  );

  const debouncedSaveEstimatedValue = useDebouncedSave(
    useCallback((value: number | null) => saveField({ estimatedValue: { value, currency: Currency.Nok } }), [saveField])
  );

  const debouncedSaveCpv = useDebouncedSave(
    useCallback((value: string) => saveField({ cpvCode: { mainCode: value || null, additionalCodes: [] } }), [saveField])
  );

  const thresholdInfo = getThresholdInfo(estimatedValue, t);

  return (
    <Box pb="8" pt="10">
      {/* Title */}
      <FormControl isRequired mb="8">
        <Flex align="center" gap="1">
          <FormLabel mb="0">{t("Title")}</FormLabel>
          {isSaving("title") && <Spinner size="xs" />}
        </Flex>
        <Input
          type="text"
          value={title}
          placeholder={t("E.g. Office supplies for municipality of Oslo") ?? undefined}
          onChange={(e) => {
            setTitle(e.target.value);
            debouncedSaveTitle(e.target.value);
          }}
        />
        <FormHelperText>{t("A short, descriptive title for the procurement")}</FormHelperText>
      </FormControl>

      {/* Description */}
      <FormControl mb="8">
        <FormLabel>{t("Description")} {isSaving("description") && <Spinner size="xs" />}</FormLabel>
        <TipTap
          content={event.eventData.description ?? ""}
          // TODO(SM-303): fjernes når saveField erstattes av koordineringslaget
          onUpdate={async (value) => { await saveField({ description: { value } }); }}
          editable={true}
          debounceWait={300}
        />
        <FormHelperText>{t("Describe the scope and requirements of this procurement")}</FormHelperText>
      </FormControl>

      {/* CPV Codes */}
      <FormControl mb="8">
        <FormLabel>{t("CPV code")} {isSaving("cpvCode") && <Spinner size="xs" />}</FormLabel>
        <Input
          type="text"
          value={cpvCode}
          placeholder={t("E.g. 30190000 - Office equipment") ?? undefined}
          onChange={(e) => {
            const value = e.target.value;
            setCpvCode(value);
            if (value === "" || /^\d{8}$/.test(value)) {
              debouncedSaveCpv(value);
            }
          }}
        />
        <FormHelperText>{t("Common Procurement Vocabulary code for this procurement")}</FormHelperText>
      </FormControl>

      {/* Estimated Value */}
      <FormControl mb="8">
        <FormLabel>{t("Estimated value")} {isSaving("estimatedValue") && <Spinner size="xs" />}</FormLabel>
        <InputGroup>
          <NumericInput
            numericValue={estimatedValue}
            placeholder="0"
            onNumericChange={(value) => {
              setEstimatedValue(value);
              debouncedSaveEstimatedValue(value);
            }}
          />
          <InputRightAddon>{t("NOK")}</InputRightAddon>
        </InputGroup>
        {thresholdInfo && (
          <Alert
            status={thresholdInfo.colorScheme as "info" | "warning"}
            mt="2"
            borderRadius="md"
            fontSize="sm"
          >
            <AlertIcon />
            {thresholdInfo.message}
          </Alert>
        )}
      </FormControl>

      {/* Contract Nature */}
      <FormControl mb="8">
        <FormLabel>{t("Contract type")} {isSaving("contractNature") && <Spinner size="xs" />}</FormLabel>
        <RadioOptionGroup
          options={CONTRACT_NATURE_OPTIONS}
          value={contractNature}
          onChange={(value) => {
            const enumValue = value as ContractNature;
            setContractNature(enumValue);
            saveField({ contractNature: enumValue });
          }}
        />
      </FormControl>

      <ContractPeriodSection eventId={eventId} event={event} />

      <FrameworkAgreementField eventId={eventId} event={event} />

      <AdvancedSettingsSection eventId={eventId} event={event} />

      <StepNavigation onNext={nextStep} nextLabel={t("Procedure")} />
    </Box>
  );
};
